import {useI18n} from 'i18n/I18nContext'
import React from 'react'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {useFormicForm} from 'TemplateEditor/Layout/Blocks'
import {BorderInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/BorderInput'
import {ColorInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/ColorInput'
import {PaddingInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/PaddingInput'
import {SwitchInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/SwitchInput'
import {
  Border,
  Padding,
  StyledTitle,
} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import LaptopMacIcon from '@mui/icons-material/LaptopMac'
import {ImageInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/ImageInput'
import {TicketTierVisibilityInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/TicketTierVisibilityInput'
import {VisibilityInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/VisibilityInput'
import {TEMPLATE_EDITOR_ACTIVE_FORM_ID} from 'TemplateEditor/templateEditorFormId'
import {EditorConfigTopBar} from 'TemplateEditor/EditorConfigTopBar'
import {humanSectionNumberFromEditKey} from 'TemplateEditor/editorPanelTitles'

export type SectionConfigType = {
  padding: Padding
  background: string | null
  backgroundImage: string | null
  border: Border
  revertMobile: boolean
  visibility: 'all' | 'desktop' | 'mobile'
  visible: boolean
  /** If non-empty, only attendees with one of these `event_tickets.id` see the section when `visible` is true. */
  visible_ticket_ids?: number[]
}

export function SectionConfig() {
  const {t} = useI18n()
  const {setEditElement, editElement, mobileMode, setMobileMode} = useEditor()

  const handleToggleMobileMode = () => setMobileMode(!mobileMode)

  const {handleSubmit, values, setFieldValue} =
    useFormicForm<SectionConfigType>()

  const sectionNum = editElement
    ? humanSectionNumberFromEditKey(editElement)
    : null
  const sectionTitle =
    sectionNum != null
      ? `${t('templateEditor.sectionLabel')} ${sectionNum}`
      : t('templateEditor.sectionLabel')

  return (
    <>
      <div className={'offcanvas__editor info-open'}>
        <div className="offcanvas__wrapper">
          <div className="offcanvas__content template-editor-config-split">
            <div className="template-editor-config-sticky">
              <EditorConfigTopBar
                itemLabel={sectionTitle}
                backLabel={t('templateEditor.backToLayout')}
                onBack={() => setEditElement(null)}
              />

              <div className={`bd-theme-toggle mb-20 d-block`}>
                <label
                  className="bd-theme-toggle-main"
                  style={{width: '100%'}}
                  htmlFor="template-editor-config-mobile-toggle"
                >
                  <span
                    className={`bd-theme-toggle-dark ${!mobileMode ? 'active' : ''}`}
                  >
                    <LaptopMacIcon style={{fontSize: 18}} />{' '}
                    {t('templateEditor.sidebarDesktop')}
                  </span>
                  <input
                    type="checkbox"
                    id="template-editor-config-mobile-toggle"
                    checked={!mobileMode}
                    onChange={handleToggleMobileMode}
                  />
                  <i className="bd-theme-toggle-slide"></i>
                  <span
                    className={`bd-theme-toggle-light ${mobileMode ? 'active' : ''}`}
                  >
                    <SmartphoneIcon style={{fontSize: 18}} />{' '}
                    {t('templateEditor.sidebarMobile')}
                  </span>
                </label>
              </div>
            </div>

            <div className="template-editor-config-scroll">
              <form id={TEMPLATE_EDITOR_ACTIVE_FORM_ID} onSubmit={handleSubmit}>
                <SwitchInput
                  value={values.visible ?? false}
                  name={'visible'}
                  label={t('templateEditor.visibleToUsers')}
                  setFieldValue={setFieldValue}
                />

                {values.visible ? (
                  <TicketTierVisibilityInput
                    value={values.visible_ticket_ids ?? []}
                    name={'visible_ticket_ids'}
                    setFieldValue={setFieldValue}
                  />
                ) : null}

                <VisibilityInput
                  value={values.visibility}
                  name={'visibility'}
                  label={t('templateEditor.visibleOnDevices')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.sectionBasics')}</StyledTitle>
                <ColorInput
                  value={values.background ?? '#ffffff'}
                  name={'background'}
                  label={t('templateEditor.backgroundColorLabel')}
                  setFieldValue={setFieldValue}
                />

                <ImageInput
                  value={values.backgroundImage}
                  name={'backgroundImage'}
                  label={t('templateEditor.backgroundImageLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.contentTitle')}</StyledTitle>

                <SwitchInput
                  value={values.revertMobile ?? false}
                  name={'revertMobile'}
                  label={t('templateEditor.swapOnMobileLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.bordersTitle')}</StyledTitle>

                <BorderInput
                  value={values.border}
                  name={'border'}
                  label={t('templateEditor.bordersFieldLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.paddingTitle')}</StyledTitle>

                <PaddingInput
                  value={values.padding}
                  name={'padding'}
                  label={t('templateEditor.paddingFieldLabel')}
                  setFieldValue={setFieldValue}
                  max={500}
                  min={0}
                />
              </form>
            </div>

            <div className="template-editor-config-footer">
              <button
                className="input__btn w-100 template-editor-save-submit"
                type="submit"
                form={TEMPLATE_EDITOR_ACTIVE_FORM_ID}
              >
                {t('templateEditor.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
